/**
 * ToastContext - Global toast notification management
 *
 * Features:
 * - Global toast state management
 * - Auto-dismiss with configurable duration
 * - Queue management for multiple toasts
 * - Type-safe toast creation
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  ReactNode,
} from 'react';
import { ToastContainer, ToastData, ToastType } from '../components/shared/Toast';

interface ToastContextType {
  toasts: ToastData[];
  addToast: (toast: Omit<ToastData, 'id'>) => string;
  removeToast: (id: string) => void;
  clearToasts: () => void;
  // Convenience methods
  success: (title: string, message: string) => string;
  error: (title: string, message: string) => string;
  info: (title: string, message: string) => string;
  warning: (title: string, message: string) => string;
  autoDeposit: (totalAmount: number, lpAmount: number, driftAmount: number) => string;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

const DEFAULT_DURATION = 5000;
const AUTO_DEPOSIT_DURATION = 8000;
const MAX_TOASTS = 5;

interface ToastProviderProps {
  children: ReactNode;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center';
}

export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  position = 'top-right',
}) => {
  const [toasts, setToasts] = useState<ToastData[]>([]);
  const timersRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Generate unique ID
  const generateId = useCallback(() => {
    return `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Remove a toast by ID
  const removeToast = useCallback((id: string) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  // Add a new toast
  const addToast = useCallback((toast: Omit<ToastData, 'id'>): string => {
    const id = generateId();
    const duration = toast.duration || DEFAULT_DURATION;

    setToasts((prev) => {
      // Remove oldest toasts if we exceed max
      const newToasts = [...prev, { ...toast, id }];
      if (newToasts.length > MAX_TOASTS) {
        // Remove the oldest toast
        const removed = newToasts.shift();
        if (removed) {
          const timer = timersRef.current.get(removed.id);
          if (timer) {
            clearTimeout(timer);
            timersRef.current.delete(removed.id);
          }
        }
      }
      return newToasts;
    });

    // Set auto-dismiss timer
    if (duration > 0) {
      const timer = setTimeout(() => {
        removeToast(id);
      }, duration);
      timersRef.current.set(id, timer);
    }

    return id;
  }, [generateId, removeToast]);

  // Clear all toasts
  const clearToasts = useCallback(() => {
    timersRef.current.forEach((timer) => clearTimeout(timer));
    timersRef.current.clear();
    setToasts([]);
  }, []);

  // Convenience methods
  const success = useCallback((title: string, message: string): string => {
    return addToast({ type: 'success', title, message });
  }, [addToast]);

  const error = useCallback((title: string, message: string): string => {
    return addToast({ type: 'error', title, message, duration: 7000 });
  }, [addToast]);

  const info = useCallback((title: string, message: string): string => {
    return addToast({ type: 'info', title, message });
  }, [addToast]);

  const warning = useCallback((title: string, message: string): string => {
    return addToast({ type: 'warning', title, message });
  }, [addToast]);

  const autoDeposit = useCallback((totalAmount: number, lpAmount: number, driftAmount: number): string => {
    return addToast({
      type: 'auto_deposit',
      title: 'Funds Automatically Deployed',
      message: `$${totalAmount.toFixed(2)} has been deployed to your positions`,
      duration: AUTO_DEPOSIT_DURATION,
      details: {
        lp_amount: lpAmount,
        drift_amount: driftAmount,
      },
    });
  }, [addToast]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      timersRef.current.forEach((timer) => clearTimeout(timer));
    };
  }, []);

  const value: ToastContextType = {
    toasts,
    addToast,
    removeToast,
    clearToasts,
    success,
    error,
    info,
    warning,
    autoDeposit,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={removeToast} position={position} />
    </ToastContext.Provider>
  );
};

export default ToastContext;
